/**
 * External dependencies
 */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';

/**
 * Internal dependencies
 */

import { requestCurrentRetailerRentalIfNotRequested } from 'state/current-retailer-rental/actions';
import { getCurrentRetailerRental, requestingCurrentRetailerRental } from 'state/current-retailer-rental/selectors';
import { requestCurrentRentalIfNotRequested } from 'state/current-rental/actions';
import { getCurrentRental, requestingCurrentRental } from 'state/current-rental/selectors';
import RentalPlanLoader from './rental-plan-loader';
import ScheduledChangesCard from './scheduled-changes';
import Icon from 'components/icon';
import Money from 'components/money';
import PlanName from 'components/plan-name';
import DateTime from 'components/date-time';
import Coupon from 'components/coupon';
import { Stack, Box, Divider, Text } from '@towersystems/towersystems-design-system';
import DetailListItem from 'components/detail-list-item';
import SubscriptionStatus from 'components/subscription-status';
import Card from 'components/card';
import FlashMessage from 'components/flash-message';

/**
 * Style dependencies
 */
import './styles.scss';

const CurrentRetailerRentalPlanCard = (props) => {
	// Retailer-rental (AU/NZ) takes precedence when present; a customer with
	// no retailer-rental subscription falls back to their plain rental plan,
	// since this page is reachable directly (bookmark/support link) by
	// plain-rental customers too, not just via the dashboard's gated button.
	const hasRetailerRental = props.currentRetailerRental && props.currentRetailerRental.length > 0;
	const rentals = hasRetailerRental ? props.currentRetailerRental : props.currentRental;
	const requesting = props.requestingRetailerRental || props.requestingRental;

	useEffect(() => {
		props.requestCurrentRetailerRental();
		props.requestCurrentRental();
	}, []);

	if ((!rentals || rentals.length === 0) && !requesting) {
		return (
			<Card>
				<Card.Header heading='Plan details' />
				<Stack>
					<Card.Section>
						<DetailListItem label='Plan amount' />
					</Card.Section>
					<Card.Section>
						<DetailListItem label='Current billing period' />
					</Card.Section>
					<Card.Section>
						<DetailListItem label='Next billing date' />
					</Card.Section>
				</Stack>
			</Card>
		);
	}

	if ((!rentals || rentals.length === 0) && requesting) {
		return <RentalPlanLoader />;
	}

	return (
		<Stack space='xxlarge'>

		    {rentals.map((rental) => {
		    	return (<Stack key={rental.subscription.id} space='medium'>
		    	<Card><Stack><Card.Section>
							<Box display='flex' padding='small' alignItems='center'>
								<Stack space='small'>
									<Box>
										<PlanName value={rental.subscription.plan_id} />
									</Box>
									<Box>
									    Price: <Money value={rental.subscription.plan_unit_price / 100} /> /{' '}
										{rental.subscription.billing_period} {rental.subscription.billing_period_unit}
						         	</Box>
						         	{rental.subscription.has_scheduled_changes == false && rental.subscription.status == 'active' && (
						         	<Box>
									    Next Billing On: <DateTime date={rental.subscription.next_billing_at} />
						         	</Box>
						         	)}
									{rental.subscription.has_scheduled_changes == true && rental.subscription.status == 'active' && (
										<Box>
											Current Term End On: <DateTime date={rental.subscription.current_term_end} />
											<Text size='xsmall' weight='strong'>* Your plan is scheduled for change on next renewal.</Text>
										</Box>
									)}

						         	{rental.subscription.addons && (
										<Box padding='small'>
											<Box marginBottom='medium'>
												<Text weight='medium' size='small'>
													Addons
												</Text>
											</Box>
											<Stack space='medium' dividers>
												{rental.subscription.addons.map(({ unit_price, quantity, amount, id }) => {
													return (
														<Box
															key={id}
															display='flex'
															alignItems='center'
															justifyContent='spaceBetween'
														>
															<Text size='small' padding='small'>
															    {id} {' '}
																<Money value={amount / 100} /> ( x {quantity})
															</Text>
														</Box>
													);
												})}
											</Stack>
										</Box>
								
								    )}

								    {rental.subscription.coupons && (
										<Box padding='small'>
											<Box marginBottom='medium'>
												<Text weight='medium' size='small'>
													Discounts
												</Text>
											</Box>
											<Stack space='medium' dividers>
												{rental.subscription.coupons.map(({ coupon_id }) => {
													return (
														<Box key={coupon_id}>
															<Coupon couponId={coupon_id}></Coupon>
														</Box>
													);
												})}
											</Stack>
										</Box>
									)}
									<Box>
										<SubscriptionStatus status={rental.subscription.status} />
									</Box>
									{rental.subscription.status === 'cancelled' && (
										<Box>
											<FlashMessage warning>To re-subscribe to a Rental plan, please contact our account team (<a href='mailto:accounts@towersystems.com.au' style={{ color: 'inherit' }}>accounts@towersystems.com.au</a>)</FlashMessage>
										</Box>
									)}
									{rental.subscription.status == 'future' && (
									<Box>
										Subscription start date: 
										{<DateTime date={rental.subscription.start_date} format='Do MMM, YYYY' />}
						         	</Box>
						         	)}
						         	
								</Stack>
							</Box>
							
						</Card.Section></Stack></Card>
					{rental.subscription.has_scheduled_changes && (
						<ScheduledChangesCard
							subscriptionId={rental.subscription.id}
							currentPrice={rental.subscription.plan_unit_price}
						/>
					)}
					</Stack>)
		    })}

		</Stack>
	);
};

export default connect(
	(state) => ({
		currentRetailerRental: getCurrentRetailerRental(state),
		requestingRetailerRental: requestingCurrentRetailerRental(state),
		currentRental: getCurrentRental(state),
		requestingRental: requestingCurrentRental(state),
	}),
	{
		requestCurrentRetailerRental: requestCurrentRetailerRentalIfNotRequested,
		requestCurrentRental: requestCurrentRentalIfNotRequested,
	},
)(CurrentRetailerRentalPlanCard);
